import React from 'react';
import styled, {StyledConfig} from 'styled-components';
import {Button, ButtonProps, CircularProgress} from '@material-ui/core';

export type BlueButtonProps =
    & ButtonProps
    & {
    isLoading?: boolean,
    hasFutureSchoolAcademicYear?: boolean
}

export const BlueButton = ({
    isLoading = false,
    hasFutureSchoolAcademicYear= false,
    ...restProps
}: BlueButtonProps) => {
    if (restProps.variant === 'outlined') {
        return <BlueButtonOutlineStyled
            {...restProps}
            {...isLoading && {
                startIcon: process.env.STORYSHOTS ? null : <WhiteProgress/>
            }}
            isLoading={isLoading}
            disabled={restProps.disabled}
        />;
    } else if (hasFutureSchoolAcademicYear) {
        return <BlueButtonViewStyled
            {...restProps}
            isLoading={isLoading}
        />
    }

    return <BlueButtonStyled
        {...restProps}
        {...isLoading && {
            startIcon: <WhiteProgress/>
        }}
        disabled={restProps.disabled}
        isLoading={isLoading}
    />;
};

const WhiteProgress = styled(CircularProgress).attrs({ size: '30px' })`
  color: white;
`;

const blueButtonStyledConfig: StyledConfig = {
    // required to pass props to styled-components css template string
    shouldForwardProp: (prop, defaultValidatorFn) =>
        !['isLoading'].includes(prop) ||
        !['startIcon'].includes(prop) &&
        defaultValidatorFn(prop)
};

const BlueButtonBase = styled(
    Button as React.FC<BlueButtonProps>
).withConfig(blueButtonStyledConfig)`
  color: white;
  border-radius: 40px;
  text-transform: revert;
  padding-left: 20px;
  padding-right: 20px;
  border-width: 4px;
  border-style: solid;
  border-color: transparent;

  ${props => props.isLoading && `
    pointer-events: none;
  `}
`;

export const BlueButtonStyled = styled(BlueButtonBase)`
  background-color: #076CAC;

  &:focus, &.__focus,
  &:hover, &.__hover {
    background-color: #2391dd;
  }

  &:focus, &.__focus {
    border-style: double !important;
    border-color: white !important;
  }

  &:disabled {
    color: white;
    background-color: #076CAC;
    opacity: 0.1;
  }
`;

export const BlueButtonOutlineStyled = styled(BlueButtonBase)`
  background-color: white;
  color: #076CAC;
  box-shadow: 0 0 0 1px #076CAC;

  &:focus, &.__focus,
  &:hover, &.__hover {
    box-shadow: 0 0 0 1px #076CAC;
    background-color: #eaf7ff;
  }

  &:focus, &.__focus {
    border-style: double !important;
    border-color: white !important;
  }

  &:disabled {
    color: white;
    background-color: #076CAC;
    opacity: 0.1;
    box-shadow: 0 0 0 1px #c0c0c0 !important;
    border-color: #c0c0c0 !important;
  }
`;

export const BlueButtonViewStyled = styled(BlueButtonBase)`
  background-color: #e1f2fd;
  color: #076CAC;
  min-width: 120px;
  padding: 6px 34.5px;
  
  &:focus, &.__focus,
  &:hover, &.__hover {
    box-shadow: 0 0 0 1px #076CAC;
    background-color: #eaf7ff;
  }

  &:focus, &.__focus {
    border-style: double !important;
    border-color: white !important;
  }

  &:disabled {
    color: #ababab;
    background-color: #c0c0c0;
    box-shadow: 0 0 0 1px #c0c0c0 !important;
    border-color: #c0c0c0 !important;
  }
`;

export const BlueSearchButton = styled(BlueButton)`
  text-transform: revert;
  padding-left: 40px;
  padding-right: 40px;
`;
